import type { AuthUser, UserType } from '@/types/auth';

const userTypes: Record<number, UserType> = {
  0: 'hqAdmin',
  1: 'branchAdmin',
  2: 'hqUser',
  3: 'branchUser',
};

export function getUserType(user: Pick<AuthUser, 'companyId' | 'masterCompanyId' | 'company'>): UserType {
  if (!user.companyId && !user.masterCompanyId) return userTypes[0];
  if (!user.company && user.masterCompanyId) return userTypes[1];
  if (user.companyId && !user.masterCompanyId) return userTypes[2];
  if (user.company?.masterCompanyId) return userTypes[3];
  return userTypes[0]; // fallback
}

/**
 * Resolves the company hierarchy for the logged-in user.
 * Replicates AngularJS login.service.ts lines 209-234.
 *
 * - Matriz (parent): companyId is null → set companyId = user.id
 * - Hijo (child): companyId is set → inherit props from user.company
 * - Sucursal: masterCompanyId chain resolution
 */
export function resolveCompanyHierarchy(user: AuthUser): AuthUser {
  const resolved = { ...user };

  resolved.userType = getUserType(user);

  if (!user.companyId) {
    // Matriz: parent company — companyId is self
    resolved.companyId = user.id;
    resolved.masterCompanyId = user.masterCompanyId || user.id;
  } else if (user.company) {
    // Hijo: child company — inherit properties from parent
    resolved.uuid = user.company.uuid;
    resolved.masterCompanyId = user.company.masterCompanyId || user.companyId;
    resolved.companyType = user.company.companyType;
    resolved.billingProvider = user.company.billingProvider;
    resolved.statusList = user.company.statusList;
    resolved.country = user.company.country;
    resolved.street = user.company.street;
    resolved.phone = user.company.phone;
    resolved.subscription = user.company.subscription;
    resolved.ordersPrefix = user.company.ordersPrefix;
    resolved.config = user.company.config;
    resolved.businessTypes = user.company.businessTypes;
    resolved.nextPayment = user.company.nextPayment;
    resolved.plan_id = user.company.plan_id;
    resolved.image = user.company.image;
    resolved.conekta_customer_id = user.company.conekta_customer_id;
    resolved.facturapi = user.company.facturapi;
    resolved.companyShortName = user.company.companyShortName;
    resolved.companyName = user.company.companyName;
  }

  // inventoryStrict is derived from businessTypes on the parent company
  const parentCompany = resolved.company || resolved;
  resolved.inventoryStrict = (parentCompany as any).businessTypes?.includes('inventory') ?? false;

  // Filter statusList to only show visible statuses
  if (resolved.statusList) {
    resolved.statusList = resolved.statusList.map(entry => ({
      ...entry,
      statusList: entry.statusList.filter(s => s.show),
    }));
  }

  return resolved;
}
